import { useLayoutEffect } from "react";
import styled from "styled-components";
import Footer from "../../components/fragments/Footer";
import NavBar from "../../components/fragments/NavBar";
import HeroBanner from "../../components/fragments/HeroBanner";

import { Container } from "../../components/styles/Layout";
import { Button } from "../../components/styles/Button";

import { useStaticSeo } from "../../hooks/useSeo";
import { STATIC_SEO } from "../../data/seo";

const Wrapper = styled.section`
    margin-bottom: 4rem;
`;
const TinyTitle = styled.h4``;
const Text = styled.p`
    max-width: 40rem;
`;

const CertificateBlock = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    align-items: flex-start;
    margin-bottom: 2rem;
`;
const CertificateFrame = styled.a`
    display: block;
    flex: none;
    width: 14rem;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    transition: 0.3s;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
        transition: 0.3s;
    }
`;
const CertificateImg = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;
const CertificateSide = styled.div`
    flex: 1 1 16rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.8rem;
`;
const DownloadButton = styled(Button)`
    width: fit-content;
`;

/* The register entry, so a visitor can confirm this without taking our word. */
const VerifyLink = styled.a`
    font-size: 0.9rem;
    text-decoration: underline;
`;

const Facts = styled.dl`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
    gap: 1rem 1.5rem;
    max-width: 40rem;
    margin: 0 0 2rem;
    dt {
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
        margin-bottom: 0.15rem;
    }
    dd {
        margin: 0;
        font-weight: 600;
    }
`;

/**
 * Data protection registration, built to match ClientMoneyProtection.js -
 * same certificate block, same download button, same page furniture.
 *
 * The PDF is a copy of the one the ICO publish for this registration at
 * ico.org.uk/ESDWebPages/RegistrationCertificate/ZA005338. Serving our own
 * copy keeps the page working if their URL moves, but it is a snapshot: the
 * registration expires 18 June 2027, and BOTH the PDF and the JPG thumbnail
 * beside it have to be replaced after each renewal or this page will quietly
 * show an expired certificate. The "check the entry on the ICO register"
 * link is always current, which is why it is on the page rather than being
 * left to the footer.
 */
export default function IcoRegistration() {
    useStaticSeo(STATIC_SEO["/ico-registration"]);
    useLayoutEffect(() => {
        document.getElementById("root").scrollTo(0, 0);
    }, []);

    return (
        <>
            <NavBar location="public" pageTitle="ICO Registration" />
            <HeroBanner
                title="Data Protection Registration"
                background="/images/pp-outside-dark.jpg"
                showRating={false}
            />
            <Wrapper>
                <Container>
                    <CertificateBlock>
                        <CertificateFrame
                            href="/legal/ICO_Data_Protection_Registration_Certificate.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <CertificateImg
                                src="/images/legal/ico-certificate.jpg"
                                alt="Property People London Ltd's ICO data protection registration certificate, reference ZA005338, valid to 18 June 2027"
                            />
                        </CertificateFrame>
                        <CertificateSide>
                            <Text>
                                Our data protection registration certificate, issued by the
                                Information Commissioner&rsquo;s Office and valid to 18 June 2027.
                            </Text>
                            <a
                                href="/legal/ICO_Data_Protection_Registration_Certificate.pdf"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <DownloadButton bright type="button">
                                    Download this as a pdf file
                                </DownloadButton>
                            </a>
                            <VerifyLink
                                href="https://ico.org.uk/ESDWebPages/Entry/ZA005338"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Check the entry on the ICO register
                            </VerifyLink>
                        </CertificateSide>
                    </CertificateBlock>

                    <Facts>
                        <div>
                            <dt>Registration reference</dt>
                            <dd>ZA005338</dd>
                        </div>
                        <div>
                            <dt>Data controller</dt>
                            <dd>Property People London Ltd</dd>
                        </div>
                        <div>
                            <dt>Registered</dt>
                            <dd>19 June 2013</dd>
                        </div>
                        <div>
                            <dt>Expires</dt>
                            <dd>18 June 2027</dd>
                        </div>
                    </Facts>

                    <TinyTitle>Registration with the Information Commissioner</TinyTitle>
                    <Text>
                        Every organisation in the UK that handles personal information has to pay a
                        data protection fee to the Information Commissioner&rsquo;s Office and
                        appear on its public register, unless it is exempt. As an estate agency we
                        hold personal data about landlords, tenants, buyers and sellers, so we are
                        registered and have been since 2013.
                    </Text>
                    <Text>
                        The ICO is the UK&rsquo;s independent regulator for data protection. Being
                        on the register is not an endorsement or a quality mark &mdash; it records
                        that we are a registered data controller, that our details are public, and
                        that we answer to the ICO for how we handle your information.
                    </Text>
                    <Text>
                        Registration is also one of the things we have to evidence each year for
                        our Propertymark{" "}
                        <a href="/client-money-protection">Client Money Protection</a> renewal,
                        alongside our client money account audit and our registrations with The
                        Property Ombudsman and HMRC for anti-money laundering supervision.
                    </Text>
                    <Text>
                        What we do with your information, how long we keep it and what you can ask
                        us to do about it are all set out in our{" "}
                        <a href="/privacy-policy">privacy policy</a>. If you think we have handled
                        your data badly, please tell us first &mdash; but you have the right to
                        complain to the ICO directly at any time.
                    </Text>
                </Container>
            </Wrapper>
            <Footer />
        </>
    );
}
